import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AlertController, IonicModule, ToastController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

interface Kategoria {
  kulcs: string; cim: string; leiras: string; alap: boolean; db: number;
  reszletek: { tabla: string; cim: string; db: number; torles: boolean }[];
  kivalasztva?: boolean;
}

/**
 *  ADMIN → SZERVÍZ → ÉV KIÜRÍTÉSE (NAPLO és BÉR).
 *
 *  Egy ügyfél egy évének minden rekordja kategóriánként törölhető (backend/ev-kiurites.js):
 *  előbb darabszámos előnézet, a megerősítéshez az ügyfél nevét be kell írni. A kiürítés
 *  mentést készít, amiből – amíg az év üres – egy kattintással visszaállítható.
 *  A modult az aktuális alrendszer adja (NAPLO vagy BER); más modulban nem értelmezett.
 */
@Component({
  selector: 'app-admin-ev-kiurites',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './ev-kiurites.component.html',
  styleUrls: ['./ev-kiurites.component.scss'],
})
export class AdminEvKiuritesComponent implements OnInit {
  modul: 'NAPLO' | 'BER' | null = null;
  ugyfelek: { id: string; nev: string; azonosito?: string }[] = [];
  ugyfelId: string | null = null;
  ev = new Date().getFullYear();
  evek: number[] = [];
  kategoriak: Kategoria[] = [];
  korabbiak: any[] = [];
  megerosites = '';
  betolt = false;
  fut = false;
  hiba = '';

  constructor(private http: HttpClient, private data: DataService, private alert: AlertController, private toast: ToastController) {}

  get modulNev(): string { return this.modul === 'BER' ? 'Bérszámfejtés' : 'Könyvelés'; }
  get ugyfelNev(): string { return this.ugyfelek.find((u) => u.id === this.ugyfelId)?.nev || ''; }
  get kivalasztottak(): Kategoria[] { return this.kategoriak.filter((k) => k.kivalasztva && k.db > 0); }
  get osszesDb(): number { return this.kivalasztottak.reduce((a, k) => a + k.db, 0); }
  private norm(s: string) { return String(s || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('hu-HU'); }
  get nevEgyezik(): boolean { return !!this.ugyfelNev && this.norm(this.megerosites) === this.norm(this.ugyfelNev); }

  async ngOnInit() {
    const mod = String((await this.data.getFromStorage('currentMode')) || '').toUpperCase();
    this.modul = mod === 'NAPLO' || mod === 'BER' ? mod : null;
    if (!this.modul) return;
    const si = await this.data.getFromStorage('sajatIntezmeny');
    const intezmeny = String(si?.intezmenyId || '');
    const aktiv = (await this.data.getFromStorage(this.modul === 'BER' ? 'berAktiv' : 'naploAktiv')) || {};
    const most = new Date().getFullYear();
    this.evek = Array.from({ length: 8 }, (_, i) => most + 1 - i);
    this.ev = Number(aktiv.ev) || most;
    if (!this.evek.includes(this.ev)) this.evek.push(this.ev);
    try {
      const lista: any[] = await firstValueFrom(this.http.get<any[]>(`/api/${this.modul === 'BER' ? 'ber' : 'naplo'}/ugyfelek/${intezmeny}`));
      this.ugyfelek = (lista || []).map((u) => ({ id: u.id, nev: u.nev, azonosito: u.azonosito }));
    } catch { this.hiba = 'Nem sikerült betölteni az ügyfeleket.'; }
    this.ugyfelId = this.ugyfelek.some((u) => u.id === aktiv.ugyfelId) ? aktiv.ugyfelId : (this.ugyfelek[0]?.id || null);
    await this.frissit();
  }

  async frissit() {
    this.kategoriak = []; this.korabbiak = []; this.megerosites = ''; this.hiba = '';
    if (!this.modul || !this.ugyfelId) return;
    this.betolt = true;
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/ev-kiurites/${this.modul}/${this.ugyfelId}/${this.ev}`));
      this.kategoriak = (r.kategoriak || []).map((k: Kategoria) => ({ ...k, kivalasztva: k.alap && k.db > 0 }));
      this.korabbiak = await firstValueFrom(this.http.get<any[]>(`/api/ev-kiurites/${this.modul}/${this.ugyfelId}`));
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült összeszámolni az év adatait.';
    } finally { this.betolt = false; }
  }

  reszletSzoveg(k: Kategoria, torles: boolean): string {
    return k.reszletek.filter((r) => r.torles === torles).map((r) => `${r.db.toLocaleString('hu-HU')} ${r.cim}`).join(', ');
  }
  darabSzoveg(d: Record<string, number>): string {
    return Object.values(d || {}).reduce((a, n) => a + Number(n || 0), 0).toLocaleString('hu-HU') + ' sor';
  }
  kategoriaNevek(s: string): string {
    return String(s || '').split(',').map((k) => this.kategoriak.find((x) => x.kulcs === k)?.cim || k).join(', ');
  }

  async kiurit() {
    if (!this.nevEgyezik || !this.kivalasztottak.length || this.fut) return;
    const a = await this.alert.create({
      header: `${this.ev}. év kiürítése`,
      message: `${this.ugyfelNev}: ${this.osszesDb.toLocaleString('hu-HU')} tétel törlődik (${this.kivalasztottak.map((k) => k.cim).join(', ')}). A mentésből visszaállítható, amíg az év üres.`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Kiürítés', role: 'destructive', cssClass: 'alert-veszely' }],
    });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'destructive') return;
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/ev-kiurites/${this.modul}/${this.ugyfelId}/${this.ev}`,
        { kategoriak: this.kivalasztottak.map((k) => k.kulcs), megerosites: this.megerosites }));
      const db: number = Object.values(r.torolve || {}).reduce((x: number, n: any) => x + Number(n || 0), 0) as number;
      this.jelez(`A(z) ${this.ev}. év kiürítve: ${db.toLocaleString('hu-HU')} sor törölve.`, 'success');
      await this.frissit();
    } catch (e: any) {
      this.jelez(e?.error?.error || 'Nem sikerült kiüríteni az évet.', 'danger');
    } finally { this.fut = false; }
  }

  async visszaallit(k: any) {
    const a = await this.alert.create({
      header: 'Visszaállítás',
      message: `A(z) ${k.ev}. év ${this.darabSzoveg(k.darabszam)} kiürített adata visszatöltődik. Csak akkor lehetséges, ha az évbe azóta nem került új adat.`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Visszaállítás', role: 'confirm' }],
    });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'confirm') return;
    this.fut = true;
    try {
      await firstValueFrom(this.http.post(`/api/ev-kiurites/visszaallit/${k.id}`, {}));
      this.jelez('Visszaállítva.', 'success');
      await this.frissit();
    } catch (e: any) {
      this.jelez(e?.error?.error || 'Nem sikerült visszaállítani.', 'danger');
    } finally { this.fut = false; }
  }

  private async jelez(message: string, color: string) {
    (await this.toast.create({ message, color, duration: 3500, position: 'top' })).present();
  }
}
